import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  InputAdornment,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import EditProductDialog from "./product-manager/EditProductDialog";
import { Add, Search, Edit, Delete, List } from "@mui/icons-material";
import { useProducts } from "../hooks/useProducts";
import { apiService } from "../api/services/apiService";
import { PaginationControls } from "./common/PaginationControls";
import type { Product } from "../types";
import { useAuth } from "../contexts/AuthContext";

const ITEMS_PER_PAGE = 10;

const ManageProducts: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { products: allProducts, loading, error } = useProducts();
  const currentUserEmail = user?.email ?? null;

  const [products, setProducts] = useState<Product[]>([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [deleteUuid, setDeleteUuid] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);

  /* -------------------- Load Products -------------------- */
  useEffect(() => {
    setProducts(allProducts);
  }, [allProducts]);

  /* -------------------- Delete Product -------------------- */
  const handleDeleteClick = (uuid: string) => {
    setDeleteUuid(uuid);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteUuid) return;

    setIsDeleting(true);
    try {
      const success = await apiService.deleteProduct(deleteUuid);
      if (success) {
        setProducts((prev) => prev.filter((p) => p.uuid !== deleteUuid));
      }
    } catch (err: any) {
      console.error(err);
      alert(err.message || "Failed to delete product");
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setDeleteUuid(null);
    }
  };

  /* -------------------- Filtering & Pagination -------------------- */
  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          Products
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/manage-assets/products/add")}
          >
            Add Product
          </Button>
        </Box>
      </Box>

      {/* Loading / Error */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Typography color="error">{error}</Typography>}

      {/* Table */}
      {!loading && !error && (
        <>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Requests</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedProducts.length ? (
                  paginatedProducts.map((product) => (
                    <TableRow key={product.uuid} hover>
                      <TableCell>{product.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={product.status}
                          size="small"
                          color={
                            product.status === "ACTIVE" ? "success" : "default"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {currentUserEmail &&
                        product.productOwners?.includes(currentUserEmail) ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<List />}
                            onClick={() =>
                              navigate("/subscription-requests", {
                                state: { productUuid: product.uuid },
                              })
                            }
                            sx={{ textTransform: "none" }}
                          >
                            View requests
                          </Button>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => setEditProduct(product)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(product.uuid)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                  <TableCell colSpan={4} align="center">
                    No products found
                  </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      <EditProductDialog
        product={editProduct}
        open={Boolean(editProduct)}
        onClose={() => setEditProduct(null)}
        onSaved={(updated) => {
          setProducts((prev) =>
            prev.map((p) => (p.uuid === updated.uuid ? updated : p)),
          );
          setEditProduct(null);
          setSuccessOpen(true);
        }}
      />

      {/* Delete Confirmation */}
      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <strong>Delete Product?</strong>
        </DialogTitle>
        <DialogContent>
          Are you sure you want to delete this product?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteModalOpen(false)}
            variant="outlined"
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            No
          </Button>
          <Button
            color="error"
            variant="contained"
            sx={{ textTransform: "none", fontWeight: 600 }}
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={successOpen}
        autoHideDuration={2000}
        onClose={() => setSuccessOpen(false)}
      >
        <Alert severity="success">Product updated successfully</Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageProducts;
